import { NetworkAssessmentAnalysisType, RegionSetStatusType } from "@app/projects/enumerators";
import { RegionGranularity } from "@app/region-sets";
import { startCase } from "lodash";
import { Component } from "react";
import { AdequacyAggregateGranularity, AdequacyAggregateType, ExportFormatType, ExportGapFilterType } from "../enumerations";
import { ExportAdequacyAggregatesModel, ExportAttestationAggregatesModel, ExportCompositeAggregatesModel, RegionSetFilter } from "../exportModels";
import ExportService from "../exportService";
import * as helpers from "./exportPanelBodyHelpers";
import { SelectFormat, SelectRegionSetFilter } from "./filters";
import GenerateBody from "./generate-body";
import { ReportPanelBodyBaseState } from "./reportPanelBodyBase";
import * as toast from "./toast";
import { ReportPanelBodyBaseProps } from "./reportPanelBodyBaseProps";

interface RegionAggregatePanelBodyState extends ReportPanelBodyBaseState {
  regionSetFilter: RegionSetFilter;
  sharedUsers: number[];
}

export default class RegionAggregatePanelBody extends Component<ReportPanelBodyBaseProps, RegionAggregatePanelBodyState> {
  private readonly exportService = new ExportService();
  private readonly testIdSuffix = "region-aggregate-panel";

  constructor(props: ReportPanelBodyBaseProps) {
    super(props);

    this.state = {
      ...this.getInitialBodyState()
    };
  }

  public componentWillUnmount() {
    toast.dismiss();
  }

  public render() {
    return (
      <>
        <div className="row">
          <div className="col-12 col-sm-6">
            <SelectRegionSetFilter
              disabled={this.props.disableRegionFilters}
              onNetworkAssessmentAnalysisTypeChange={this.handleNetworkAssessmentAnalysisTypeChange}
              onRegionSetFilterTypeChange={this.handleRegionSetFilterTypeChange}
              onRegionSetIncludeChange={this.handleRegionSetIncludeChange}
              regionSetFilter={this.state.regionSetFilter}
              singularDisplay={this.props.singularDisplay}
              pluralDisplay={this.props.pluralDisplay}
              testIdSuffix={this.testIdSuffix}
            />
          </div>
          <div className="col-12 col-sm-6">
            <SelectFormat outputFormat={this.state.outputFormat} onOutputFormatChange={this.handleOutputFormatChange} excelDisabled={false} exportType="region-aggregate" />
          </div>
        </div>
        <GenerateBody
          {...this.props}
          emailOnSuccess={this.state.emailOnSuccess}
          reportName={this.state.reportName}
          onGenerateClick={this.handleGenerateClick}
          onEmailOnSuccessChange={this.handleEmailOnSuccessChange}
          onReportNameChange={this.handleReportNameChange}
          isLoading={this.state.loadingReportSettingsJSONStrings && this.state.loadingReportSettingsJSONStrings.indexOf(helpers.stringifyStateReportSettings(this.state)) > -1}
          isValid={this.state.regionSetFilter.includeActive || this.state.regionSetFilter.includePending || this.state.regionSetFilter.includeApproved}
          showSharedUsers={this.props.match.params.module === "adequacy"}
          sharedUsers={this.state.sharedUsers}
          updateSelectedSharedUsers={this.updateSharedUsers}
          exportType="AdequacyAggregate"
          projectId={this.props.projectId}
          networkAssessmentAnalysisType={this.state.regionSetFilter.networkAssessmentAnalysisType}
          aggregateType={AdequacyAggregateType.RegionSet}
        />
      </>
    );
  }

  private getInitialBodyState = (): RegionAggregatePanelBodyState => {
    return {
      emailOnSuccess: true,
      loadingReportSettingsJSONStrings: [],
      outputFormat: ExportFormatType.Excel,
      regionSetFilter: {
        filterType: ExportGapFilterType.All,
        includeActive: true,
        includeApproved: true,
        includePending: true,
        networkAssessmentAnalysisType: this.props.initialNetworkAssessmentAnalysisType,
      },
      reportName: helpers.reportName(this.props.comparedRunConfigurationId !== null ? startCase(this.props.granularity) + " Comparison" : startCase(this.props.granularity)),
      sharedUsers: [],
    } as RegionAggregatePanelBodyState;
  }

  private handleRegionSetFilterTypeChange = (filterType: ExportGapFilterType) => {
    this.setState((prevState) => ({
      ...prevState,
      regionSetFilter: {
        ...prevState.regionSetFilter,
        filterType,
      },
      reportSettingsHasChanges: true
    }));
  }

  private handleRegionSetIncludeChange = (selected: boolean, regionSetStatus: RegionSetStatusType) => {
    let activeSelected: boolean = this.state.regionSetFilter.includeActive;
    let approvedSelected: boolean = this.state.regionSetFilter.includeApproved;
    let pendingSelected: boolean = this.state.regionSetFilter.includePending;

    switch (regionSetStatus) {
      case RegionSetStatusType.Active:
        activeSelected = selected;
        break;
      case RegionSetStatusType.Approved:
        approvedSelected = selected;
        break;
      case RegionSetStatusType.Pending:
        pendingSelected = selected;
        break;
    }

    this.setState((prevState) => ({
      ...prevState,
      regionSetFilter: {
        ...prevState.regionSetFilter,
        includeActive: activeSelected,
        includeApproved: approvedSelected,
        includePending: pendingSelected,
      },
      reportSettingsHasChanges: true
    }));
  }

  private handleNetworkAssessmentAnalysisTypeChange = (networkAssessmentAnalysisType: NetworkAssessmentAnalysisType) => {
    this.setState((prevState) => ({
      ...prevState,
      regionSetFilter: {
        ...prevState.regionSetFilter,
        networkAssessmentAnalysisType,
      },
      reportSettingsHasChanges: true
    }));
  }

  private handleOutputFormatChange = (outputFormat: ExportFormatType) => {
    this.setState({ outputFormat });
  }

  private handleGenerateClick = async () => {
    const stateReportSettingsJSON = helpers.stringifyStateReportSettings(this.state);
    this.setState((prevState) => ({
      ...prevState,
      loadingReportSettingsJSONStrings: [
        ...prevState.loadingReportSettingsJSONStrings,
        stateReportSettingsJSON,
      ],
    }));

    switch (this.props.match.params.module) {
      case "adequacy":
        await this.generateExportAdequacyAggregateModel();
        break;
      case "attestation":
        await this.generateExportAttestationAggregateModel();
        break;
      case "composite":
        await this.generateExportCompositeAggregateModel();
        break;
      default:
        break;
    }

    toast.toastInfo(this.state.reportName);
  }

  // Adequacy
  private generateExportAdequacyAggregateModel = async () => {
    const aggregateModel: ExportAdequacyAggregatesModel = {
      aggregateGranularity: this.getAdequacyAggregateGranularity(this.props.granularity),
      aggregateType: AdequacyAggregateType.RegionSet,
      comparedProjectRunConfigurationId: this.props.comparedRunConfigurationId,
      customProjectId: this.props.projectId,
      eMailOnSuccess: this.state.emailOnSuccess,
      exportName: this.state.reportName,
      formatType: this.state.outputFormat,
      includeActiveRegionSets: this.state.regionSetFilter.includeActive,
      includeApprovedRegionSets: this.state.regionSetFilter.includeApproved,
      includeFacilitySpecialties: true,
      includePendingRegionSets: this.state.regionSetFilter.includePending,
      includePhysicianSpecialties: true,
      networkAssessmentAnalysisTypes: [this.state.regionSetFilter.networkAssessmentAnalysisType],
      projectRunConfigurationId: this.props.runConfigurationId,
      projectURL: this.props.projectURL,
      regionSetGapFilter: this.state.regionSetFilter.filterType,
      regionSetIds: this.props.regionSetIds,
      runAsynchronous: true,
      sharedUsers: this.state.sharedUsers,
      specialtyGapFilter: ExportGapFilterType.All,
      specialtyGroupCodes: this.props.specialtyGroupCodes,
    };
    await this.exportService.postExportAdequacyAggregates(aggregateModel);
  }

  // Attestation
  private generateExportAttestationAggregateModel = async () => {
    const aggregateModel: ExportAttestationAggregatesModel = {
      customProjectId: this.props.projectId,
      eMailOnSuccess: this.state.emailOnSuccess,
      exportName: this.state.reportName,
      formatType: this.state.outputFormat,
      includeActiveRegionSets: this.state.regionSetFilter.includeActive,
      includeApprovedRegionSets: this.state.regionSetFilter.includeApproved,
      includeFacilitySpecialties: true,
      includePendingRegionSets: this.state.regionSetFilter.includePending,
      includePhysicianSpecialties: true,
      projectRunConfigurationId: this.props.runConfigurationId,
      projectURL: this.props.projectURL,
      regionSetGapFilter: this.state.regionSetFilter.filterType,
      regionSetIds: this.props.regionSetIds,
      runAsynchronous: true,
      specialtyGapFilter: ExportGapFilterType.All,
      specialtyGroupCodes: this.props.specialtyGroupCodes,
    };
    await this.exportService.postExportAttestationAggregates(aggregateModel);
  }

  // Composite
  private generateExportCompositeAggregateModel = async () => {
    const aggregateModel: ExportCompositeAggregatesModel = {
      customProjectId: this.props.projectId,
      eMailOnSuccess: this.state.emailOnSuccess,
      exportName: this.state.reportName,
      formatType: this.state.outputFormat,
      includeActiveRegionSets: this.state.regionSetFilter.includeActive,
      includeApprovedRegionSets: this.state.regionSetFilter.includeApproved,
      includeFacilitySpecialties: true,
      includePendingRegionSets: this.state.regionSetFilter.includePending,
      includePhysicianSpecialties: true,
      projectRunConfigurationId: this.props.runConfigurationId,
      projectURL: this.props.projectURL,
      regionSetGapFilter: this.state.regionSetFilter.filterType,
      regionSetIds: this.props.regionSetIds,
      runAsynchronous: true,
      specialtyGapFilter: ExportGapFilterType.All,
      specialtyGroupCodes: this.props.specialtyGroupCodes,
    };
    await this.exportService.postExportCompositeAggregates(aggregateModel);
  }

  private handleEmailOnSuccessChange = (emailOnSuccess: boolean) => {
    this.setState({ emailOnSuccess });
  }

  private handleReportNameChange = (reportName: string) => {
    this.setState({ reportName });
  }

  private updateSharedUsers = (sharedUsers: number[]) => {
    this.setState({ sharedUsers });
  }

  private getAdequacyAggregateGranularity = (regionGranularity: RegionGranularity) => {
    const x = regionGranularity;
    // AdequacyAggregateGranularity is a superset of RegionGranularity and
    // has one value - ServiceArea - that doesn't exist on RegionGranularity
    // That just means an implicit conversion doesn't work
    return x as unknown as AdequacyAggregateGranularity;
  }
}
